import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useHasAppliedFilters } from '../../../controllers/top-filter/index.tsx';
import magnifyingGlass from './assets/magnifying_glass.svg';
import magnifyingGlassError from './assets/magnifying_glass_error.svg';
import messages from './messages.tsx';

const NoMatches = () => {
	const [isFilterActive, filterStoreActions] = useHasAppliedFilters();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (
		<Container>
			{isFilterActive ? (
				<>
					<img src={magnifyingGlass} alt="" width="36" height="36" />
					<HeadingWrapper>
						<Heading size="medium" as="span">
							<HeadingText>{formatMessage(messages.noResultsWithFilters)}</HeadingText>
						</Heading>
						<Heading size="xxsmall" as="span">
							<Explanation>{formatMessage(messages.explanationActiveFilter)}</Explanation>
						</Heading>
					</HeadingWrapper>
					<Box xcss={buttonWrapperStyles}>
						<Button
							appearance="primary"
							onClick={() => {
								filterStoreActions.resetAllFilters();
							}}
						>
							{formatMessage(messages.clearFilter)}
						</Button>
					</Box>
				</>
			) : (
				<MessageContainer>
					<IconContainer>
						<img src={magnifyingGlassError} alt="" width="36" height="36" />
					</IconContainer>
					<div>
						<Heading size="medium" as="span">
							<HeadingText>{formatMessage(messages.noResultsHeading)}</HeadingText>
						</Heading>
						<div>
							{formatMessage(messages.notEnoughDataMessage)}{' '}
							<Link
								href="https://support.atlassian.com/jira-software-cloud/docs/strategies-to-reduce-your-cycle-time/#Prerequisites"
								target="_blank"
								onClick={() => {
									// NOTE - we would only expect to see this if the project had deployments and commits
									// previously but haven't received any in the last 12 weeks.
									fireUIAnalytics(
										createAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'cycleTimeLearnHowToConnectCICD',
										}),
									);
								}}
							>
								{formatMessage(messages.learnMore)}
							</Link>
						</div>
					</div>
				</MessageContainer>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	paddingTop: token('space.200'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	marginRight: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	maxWidth: '700px',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingText = styled.span({
	color: token('color.text'),
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Explanation = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.150'),
});

const buttonWrapperStyles = xcss({
	marginLeft: 'space.400',
});

export default NoMatches;
